import React, { useState } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { componentRegistry } from '../builder-components';

const StylePanel = ({ component, onUpdate, onClose }) => {
  const [expandedSections, setExpandedSections] = useState(['content', 'style']);
  
  const componentConfig = componentRegistry[component.type];
  const propDefinitions = componentConfig?.builderConfig?.props || {};

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handlePropChange = (propName, value) => {
    onUpdate({
      props: {
        ...component.props,
        [propName]: value
      }
    });
  };

  const handleStyleChange = (styleName, value) => {
    onUpdate({
      styles: {
        ...component.styles,
        [styleName]: value
      }
    });
  };

  const renderPropInput = (propName, propConfig) => {
    const value = component.props?.[propName] ?? propConfig.default ?? '';

    switch (propConfig.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handlePropChange(propName, e.target.value)}
            placeholder={propConfig.placeholder || propConfig.label}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handlePropChange(propName, e.target.value)}
            placeholder={propConfig.placeholder || propConfig.label}
            className="w-full px-3 py-2 border rounded-md text-sm min-h-[100px] resize-y"
          />
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handlePropChange(propName, e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
          >
            {propConfig.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      
      case 'color':
        return (
          <div className="flex gap-2">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => handlePropChange(propName, e.target.value)}
              className="w-10 h-10 rounded border cursor-pointer"
            />
            <Input
              value={value}
              onChange={(e) => handlePropChange(propName, e.target.value)}
              placeholder="#000000"
              className="flex-1"
            />
          </div>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handlePropChange(propName, e.target.value)}
            min={propConfig.min}
            max={propConfig.max}
            step={propConfig.step || 1}
          />
        );
      
      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handlePropChange(propName, e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm">{propConfig.label}</span>
          </label>
        );
      
      case 'image':
        return (
          <div className="space-y-2">
            <Input
              value={value}
              onChange={(e) => handlePropChange(propName, e.target.value)}
              placeholder="Image URL"
            />
            {value && (
              <img 
                src={value} 
                alt="Preview" 
                className="w-full h-20 object-cover rounded border"
              />
            )}
          </div>
        );
      
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handlePropChange(propName, e.target.value)}
          />
        );
    }
  };

  const Section = ({ title, name, children }) => {
    const isExpanded = expandedSections.includes(name);
    return (
      <div className="border-b">
        <button
          onClick={() => toggleSection(name)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
        >
          <span className="font-medium text-sm">{title}</span>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full overflow-y-auto">
      <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
        <h3 className="font-semibold text-gray-900">{component.type} Settings</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X size={18} />
        </button>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList className="w-full grid grid-cols-2 m-2">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="style">Style</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-0">
          {Object.entries(propDefinitions).length > 0 ? (
            Object.entries(propDefinitions).map(([propName, propConfig]) => (
              <div key={propName} className="px-4 py-3 border-b">
                <Label className="text-xs text-gray-500 mb-2 block">
                  {propConfig.label || propName}
                </Label>
                {renderPropInput(propName, propConfig)}
              </div>
            ))
          ) : (
            <div className="p-4 text-sm text-gray-500 text-center">
              No configurable properties
            </div>
          )}
        </TabsContent>

        <TabsContent value="style" className="mt-0">
          <Section title="Spacing" name="spacing">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Padding Top</Label>
                <Input
                  value={component.styles?.paddingTop || ''}
                  onChange={(e) => handleStyleChange('paddingTop', e.target.value)}
                  placeholder="0px"
                />
              </div>
              <div>
                <Label className="text-xs">Padding Bottom</Label>
                <Input
                  value={component.styles?.paddingBottom || ''}
                  onChange={(e) => handleStyleChange('paddingBottom', e.target.value)}
                  placeholder="0px"
                />
              </div>
              <div>
                <Label className="text-xs">Margin Top</Label>
                <Input
                  value={component.styles?.marginTop || ''}
                  onChange={(e) => handleStyleChange('marginTop', e.target.value)}
                  placeholder="0px"
                />
              </div>
              <div>
                <Label className="text-xs">Margin Bottom</Label>
                <Input
                  value={component.styles?.marginBottom || ''}
                  onChange={(e) => handleStyleChange('marginBottom', e.target.value)}
                  placeholder="0px"
                />
              </div>
            </div>
          </Section>

          <Section title="Background" name="background">
            <div>
              <Label className="text-xs">Background Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={component.styles?.backgroundColor || '#ffffff'}
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={component.styles?.backgroundColor || ''}
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  placeholder="transparent"
                  className="flex-1"
                />
              </div>
            </div>
          </Section>

          <Section title="Typography" name="typography">
            <div>
              <Label className="text-xs">Text Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={component.styles?.color || '#000000'}
                  onChange={(e) => handleStyleChange('color', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={component.styles?.color || ''}
                  onChange={(e) => handleStyleChange('color', e.target.value)}
                  placeholder="inherit"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Text Align</Label>
              <select
                value={component.styles?.textAlign || 'left'}
                onChange={(e) => handleStyleChange('textAlign', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm mt-1"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StylePanel;
